import styled, { css } from 'styled-components'
import type { TableCellItemPositionerProps, TableUIProps, CommonCellProps } from './types'

export const TableHeaderSelectionWrapper = styled.div<{ $visible: boolean }>`
  display: block;
  opacity: 0;
  position: absolute;
  z-index: 5;
  left: -44px;
  top: 50%;
  transform: translateY(-50%);

  width: 48px;
  padding: var(--space-200);

  &:hover {
    opacity: 1;
  }

  ${({ $visible }) =>
    $visible &&
    css`
      opacity: 1;
    `}
`

export const TableHeadTr = styled.tr`
  position: relative;

  &:hover {
    ${TableHeaderSelectionWrapper} {
      opacity: 1;
    }
  }
`

export const TableHead = styled.thead`
  font-size: var(--font-size-300);
  font-style: normal;
  line-height: var(--font-line-height-300);
  letter-spacing: var(--font-letter-spacing-loose);

  color: var(--text-secondary);
  user-select: none;
`

type CommonCellStylesProps = {
  $alignContent?: CommonCellProps['alignContent']
  $textAlign?: CommonCellProps['textAlign']
  $width?: CommonCellProps['width']
  $borderTop?: CommonCellProps['borderTop']
  $position?: CommonCellProps['position']
  $verticalAlign?: CommonCellProps['verticalAlign']
  $verticalSpacing?: CommonCellProps['verticalSpacing']
  $horizontalSpacing?: CommonCellProps['horizontalSpacing']
}

const CommonCellStyles = css<CommonCellStylesProps>`
  font-weight: var(--font-weight-regular);

  ${({ $textAlign }) => css`
    text-align: ${$textAlign || 'center'};
  `}

  padding: ${({ $verticalSpacing = 'var(--space-200)', $horizontalSpacing = '0' }) =>
    `${$verticalSpacing} ${$horizontalSpacing}`};

  ${({ $width }) =>
    $width &&
    css`
      width: ${$width};
    `}

  ${({ $borderTop }) =>
    $borderTop &&
    css`
      border-top: ${$borderTop} !important;
    `}

  ${({ $position }) =>
    $position &&
    css`
      position: ${$position};
    `}

  ${({ $verticalAlign }) =>
    $verticalAlign &&
    css`
      vertical-align: ${$verticalAlign};
    `}
`

export const TableHeadTh = styled.th<CommonCellStylesProps>`
  ${CommonCellStyles}
`

export const TableBodyTd = styled.td<CommonCellStylesProps & { $height?: string }>`
  ${CommonCellStyles};

  // TODO: this is temporary solution, we need to set this value dynamically to enable rows height customization
  // rel: https://popshop.atlassian.net/browse/PRD-2628
  height: ${({ $height }) => $height || '80px'};
`

export const TableCellItemPositioner = styled.div<{
  $position: TableCellItemPositionerProps['position']
  $width: TableCellItemPositionerProps['width']
  $height: TableCellItemPositionerProps['height']
}>`
  width: ${({ $width }) => $width || 'auto'};
  height: ${({ $height }) => $height || 'auto'};

  ${({ $position }) => {
    if ($position === 'start') {
      return css`
        margin-left: 0;
        margin-right: auto;
      `
    }

    if ($position === 'end') {
      return css`
        margin-right: 0;
        margin-left: auto;
      `
    }

    return css`
      margin: 0 auto;
    `
  }}
`

export const TableLoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: var(--space-2000) auto;
`

export const TableHeaderRoot = styled.div`
  position: relative;
`

export const TableHeaderActions = styled.div`
  display: flex;
  flex-direction: row;

  height: 30px;

  & > * + * {
    margin-left: var(--space-300);
  }
`

export const TableHeaderActionsItems = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--space-200);
`

export const TableHeaderActionsHeading = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
`

export const TableHeaderActionsItem = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  outline: none;

  color: var(--text-primary);
  font-size: var(--font-size-300);
  font-style: normal;
  font-weight: var(--font-weight-semibold);
  line-height: var(--font-line-height-300);

  background: var(--bg-secondary);

  padding: var(--space-100) var(--space-250);

  &:hover {
    background: var(--bg-tertiary);
  }
`

export const TableRowActionsContainer = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 100%;

  padding: var(--space-200);
  margin: var(--space-500) 0;

  color: var(--text-secondary);
  cursor: pointer;

  &:hover,
  &:focus,
  &:active {
    color: var(--text-primary);
    background: var(--bg-transparent);
    height: 32px;
  }
`

export const TableRowActionsWrapper = styled.div`
  display: block;
  opacity: 0;
  position: absolute;
  z-index: 5;
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    opacity: 1;
  }
`

export const TableRowDragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: -77px;
  top: 50%;
  width: 32px;
  padding: var(--space-200);
  height: 100%;

  transform: translateY(-50%);

  opacity: 0;
  color: var(--text-secondary);

  cursor: move;
  cursor: grab;

  &:hover {
    color: var(--text-primary);
    opacity: 1;
  }
`

export const TableRowSelectionWrapper = styled.td<{ $visible: boolean }>`
  display: flex;
  justify-content: center;
  flex-direction: column;

  opacity: 0;

  position: absolute;
  left: -44px;
  top: 50%;
  transform: translateY(-50%);

  height: 100%;
  width: 44px;

  padding-left: var(--space-200);

  z-index: 5;

  &:hover {
    opacity: 1;
  }

  ${({ $visible }) =>
    $visible &&
    css`
      opacity: 1;
    `}
`

export const TableBody = styled.tbody<{ $isLoading?: boolean }>`
  position: relative;

  width: auto;

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      opacity: 0.5;
    `}
`

export const TableBodyTr = styled.tr<{ $isDragged?: boolean; $isSelected?: boolean }>`
  position: relative;

  &:hover {
    ${TableRowActionsWrapper}, ${TableRowDragHandle}, ${TableRowSelectionWrapper} {
      opacity: 1;
    }

    z-index: 6;
  }

  ${({ $isDragged }) =>
    $isDragged &&
    css`
      background-color: var(--bg-primary);
      background-image: linear-gradient(
        270deg,
        rgba(255, 255, 255, 0) 0,
        var(--bg-secondary) 25%,
        var(--bg-secondary) 71%,
        rgba(255, 255, 255, 0) 100%
      );
    `}

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      background-image: linear-gradient(
        90deg,
        var(--bg-primary) 0%,
        var(--bg-secondary) 44.27%,
        var(--bg-secondary) 54.17%,
        var(--bg-primary) 100%
      );
    `}
`

export const Root = styled.table<{
  $hoverable: TableUIProps['hoverable']
  $minWidth: TableUIProps['minWidth']
}>`
  width: 100%;

  ${({ $minWidth }) =>
    $minWidth &&
    css`
      min-width: ${$minWidth};
    `}

  border-collapse: collapse;

  & > ${TableHead} > tr > th {
    border-bottom: 1px solid var(--border-tertiary);
  }

  & > tfoot > tr > th,
  & > ${TableBody} > tr > th,
  & > ${TableBody} > tr > td {
    border-top: 1px solid var(--border-tertiary);
  }

  & > ${TableBody} > tr:first-of-type > td,
  & > ${TableBody} > tr:first-of-type > th {
    border-top: none;
  }

  & > ${TableBody} > tr {
    ${({ $hoverable = true }) =>
      $hoverable &&
      css`
        cursor: pointer;

        &:hover {
          background-image: linear-gradient(
            270deg,
            rgba(255, 255, 255, 0) 0,
            var(--bg-secondary) 25%,
            var(--bg-secondary) 71%,
            rgba(255, 255, 255, 0) 100%
          );
        }
      `};
  }
`
